import React from 'react'
import { createPortal } from 'react-dom'

import Button from 'components/Form/Button'
import ModalWrapper from 'components/Modal/Wrapper'
import FormCore from 'components/Form/Core'
import useManualApproval from './manualApprovalController'
import SalesforceApprovalReview from './SalesforceApprovalReview'
import {
  ConfirmationNote,
  DialogActions,
  DialogBody,
  ErrorText,
  OrganizationVerificationPanel,
  ReviewDetails,
} from './styled'

export default function ManualCustomerApprovalDialog({
  row,
  loading,
  loadCompanyOptions,
  loadIdentityReview,
  onClose,
  onConfirm,
}) {
  const { t, formItems, selected, useRegistrationCompany, error, handleConfirm, salesforce, confirmationDisabled, submitting } = useManualApproval({
    row, loading, loadCompanyOptions, loadIdentityReview, onClose, onConfirm,
  })

  return createPortal(
    <ModalWrapper
      title={t('user_approvals_confirm_title')}
      text={t(row?.approvalFlow === 'partner' ? 'user_approvals_partner_description' : 'user_approvals_confirm_description')}
      modalWidth="min(680px, 100%)"
      belowHeader
      viewportSpacing
      onClose={onClose}
    >
      <DialogBody>
        <ReviewDetails>
          <div><span>{t('user_approvals_name')}</span><strong>{row?.userName}</strong></div>
          <div><span>{t('user_approvals_email')}</span><strong>{row?.userEmail}</strong></div>
          <div><span>{t('user_approvals_company_informed')}</span><strong>{row?.companyDisplayName || row?.companyName || '-'}</strong></div>
          <div><span>{t('user_approvals_country')}</span><strong>{row?.country || '-'}</strong></div>
          {row?.state ? <div><span>{t('user_approvals_region')}</span><strong>{row.state}</strong></div> : null}
          {row?.userPhone ? <div><span>{t('user_approvals_phone')}</span><strong>{row.userPhone}</strong></div> : null}
        </ReviewDetails>

        {row?.approvalFlow !== 'partner' && !loadIdentityReview ? (
          <OrganizationVerificationPanel>
            <h2>{t('user_approvals_organization_verification_title')}</h2>
            <p>{t('user_approvals_organization_verification_intro')}</p>
            <p>{t('user_approvals_organization_verification_context')}</p>
            <h3>{t('user_approvals_organization_verification_steps_title')}</h3>
            <ol>
              <li>
                <strong>{t('user_approvals_organization_verification_review_title')}</strong>
                <p>{t('user_approvals_organization_verification_review_text')}</p>
              </li>
              <li>
                <strong>{t('user_approvals_organization_verification_select_title')}</strong>
                <p>{t('user_approvals_organization_verification_select_text')}</p>
              </li>
              <li>
                <strong>{t('user_approvals_organization_verification_no_match_title')}</strong>
                <p>{t('user_approvals_organization_verification_no_match_text')}</p>
                <p>{t('user_approvals_organization_verification_reject_text')}</p>
              </li>
            </ol>
            <p className="important"><strong>{t('user_approvals_important')} </strong>{t('user_approvals_organization_verification_important')}</p>
          </OrganizationVerificationPanel>
        ) : null}

        {loadIdentityReview ? <SalesforceApprovalReview salesforce={salesforce} loading={loading || submitting} t={t} /> : null}
        {(!salesforce.review?.requiresSelection || (salesforce.review?.version === 2 && !salesforce.review.partnerFlow)) && (!salesforce.reviewLoading || salesforce.review) && !salesforce.reviewError ? <FormCore formItems={formItems} nospace /> : null}
        {error ? <ErrorText>{error}</ErrorText> : null}
        {!salesforce.review?.requiresSelection && useRegistrationCompany ? (
          <ConfirmationNote>
            {t('user_approvals_registered_company_confirmation')} <strong>{row.companyName}</strong>
          </ConfirmationNote>
        ) : !salesforce.review?.requiresSelection && salesforce.review?.version !== 2 && selected ? (
          <ConfirmationNote>
            {t('user_approvals_association_confirmation')} <strong>{selected.name}</strong>
          </ConfirmationNote>
        ) : null}

        <DialogActions>
          <Button outline color="blue" width="fit-content" disabled={loading || submitting} onClick={onClose}>
            {t('cancel')}
          </Button>
          <Button
            color="blue"
            width="fit-content"
            loading={loading || submitting}
            disabled={confirmationDisabled}
            onClick={handleConfirm}
          >
            {t('user_approvals_confirm_approval')}
          </Button>
        </DialogActions>
      </DialogBody>
    </ModalWrapper>,
    document.body
  )
}
